import type { PropsWithChildren, ReactElement } from 'react';
import { summarizeCapacity } from './helpers/summarizeCapacity';
import { summarizePricing } from './helpers/summarizePricing';
import styles from './PackageContainer.module.scss';
import { Accordion } from 'common/components/Accordion';
import { impossible } from 'common/types/impossible';
import type { Package, PackageVisibilityState } from 'packages/types';

function visibilityLabel(state: PackageVisibilityState): string | null {
  switch (state) {
    case 'draft':
      return __('packages.visibility.draft');
    case 'hidden':
      return __('packages.visibility.hidden');
    case 'published':
      return null;
    default:
      return impossible(state);
  }
}

type Props<Pkg extends Package> = PropsWithChildren<{
  pkg: Pkg;
  onPackageViewed?: (pkg: Package) => void;
}>;

export function PackageContainer<Pkg extends Package>({
  pkg,
  children,
  onPackageViewed
}: Props<Pkg>): ReactElement {
  const visibleCallback = (visible: boolean) => {
    if (visible && onPackageViewed) {
      onPackageViewed(pkg);
    }
  };

  const label = visibilityLabel(pkg.visibilityState);

  return (
    <Accordion
      heading={({ isOpen }) => (
        <div className={styles.container}>
          <div className={styles.name}>
            {label ? (
              <span className={styles.visibilityLabel}>{label}</span>
            ) : null}
            <span>{pkg.name}</span>
          </div>
          <div className={styles.capacity}>
            {summarizeCapacity(pkg.capacity)}
          </div>
          <div className={styles.pricingAndAccordionToggle}>
            <div className={styles.pricing}>
              {summarizePricing(pkg.pricing)}
            </div>
            <div
              className={styles.accordionToggle}
              data-alternative-text={
                isOpen
                  ? __('packages.accordion.show')
                  : __('packages.accordion.hide')
              }
            >
              <span className={styles.accordionToggleText}>
                {isOpen
                  ? __('packages.accordion.hide')
                  : __('packages.accordion.show')}
              </span>
            </div>
          </div>
        </div>
      )}
      body={<div className={styles.packageContents}>{children}</div>}
      onToggle={visibleCallback}
    />
  );
}
